import {
  clamp,
  type QueriesObject,
  type QueryResults,
  type System,
} from '../../odie';
import { BlockComponent } from '../components/BlockComponent';
import { WallBlockComponent } from '../components/WallBlockComponent';
import { BlockEntity, WallBlockEntity } from '../entities/BlockEntity';
import type { GameScene } from '../GameScene';
import { TravelHitEffect } from '../actions/effects/TravelHitEffect';
import { DespawnSource } from './DespawnSystem';

/*
  Handles wall block specifics.
*/
export class WallBlockSystem implements System {
  public static readonly NAME = 'blockWall';
  public static Queries: QueriesObject = {
    block: {
      components: [BlockComponent, WallBlockComponent],
    },
  };
  public scene!: GameScene;
  public queries!: QueryResults;
  public animationCount = 0;

  public start() {
    // Walls are hit by basic blocks, so they need to animate these matched basic blocks before despawning them
    this.scene.despawnSystem.addDespawnInterceptor(this);
  }

  public async interceptDespawn(entity: BlockEntity, source: DespawnSource) {
    const isBasiBlock = entity.c.block.props.type === 'basic';
    // Skip if not a basic block
    if (!isBasiBlock) return false;
    // Find a suitable target wall for the block
    const target = this.getTarget();
    // Skip if there is no valid target
    if (!target) return false;
    // Animate the block hitting the target wall
    await this.hitTarget(entity, target, source === 'mergeEffect' ? 0.1 : 0);
    // Return true to tell that the despawn has been intercepted
    return true;
  }

  private getTarget(): WallBlockEntity | null {
    const walls = this.queries.block.entities.slice() as WallBlockEntity[];
    // Skip if there are no walls left
    if (!walls.length) return null;
    // Find the first wall with a hitPoints > 0
    const selected = walls.find((wall) => wall.c.blockWall.hitPoints > 0);
    // Return the selected wall or null
    return selected ?? null;
  }

  private async hitTarget(
    block: BlockEntity,
    target: WallBlockEntity,
    delayBetweenBlocks: number,
  ) {
    if (!target) return;
    // Decrement the wall hit points, for target selecting purposes. The actual hit will happen after the animation
    target.c.blockWall.hitPoints -= 1;
    // Calculate delay based on animation count
    const delay = clamp(delayBetweenBlocks * this.animationCount, 0, 0.5);
    // Increment animation count for calculating the delay
    this.animationCount += 1;
    await new TravelHitEffect(this.scene, {
      block,
      target: {
        x: target.x + target.c.blockWall.getViewWidth() / 2 - 30,
        y: target.y + target.c.blockWall.getViewHeight() / 2 - 30,
      },
      duration: 0.4,
      delay,
    }).execute();
    // Hit the wall, actually decrementing the counter
    target.c.block.collide('goal');
    // Decrement animation count after finish
    this.animationCount -= 1;
  }
}
